/**
 * bhiSmartPriorities.js — iter998 (Feb 2026)
 *
 * Given a BHI snapshot + DKG hereditary result, decide which BhiSection
 * testids should be auto-expanded when the user clicks
 * "Разгъни моите приоритети" (Smart Expand Priorities).
 *
 * Heuristic mapping (patient-facing, non-diagnostic):
 *   • DKG hereditary tier ≥ moderate → section-hereditary
 *   • Genetic mutation flagged in profile (BRCA1/2, PALB2, CHEK2, …) →
 *     section-hereditary + section-my-data (labs / genetic docs)
 *   • BC subtype declared → section-bc-subtype
 *   • NIS avg > 55 or DIL avg > 65 → section-nutritional-fingerprint
 *   • Metabolic band red/orange → section-metabolic
 *   • Any pathway with score ≥ 0.5 → section-prevention-path
 *   • Always include section-ai-summary and section-top-stats.
 *
 * Returns an ordered list of testids (dedup'd). The first testid is the
 * scroll anchor.
 */

const num = (v) => (typeof v === "number" ? v : parseFloat(v));

export function computeSmartPriorityTestids({ snapshot, hereditary } = {}) {
  const out = new Set();
  // Always surface the patient-friendly summary + KPI tile row.
  out.add("section-ai-summary");
  out.add("section-top-stats");

  const sig = snapshot?.signals || {};
  const nis = num(sig.nis_avg);
  const dil = num(sig.dil_avg);

  // 1) Nutrition high-burden signals.
  if ((Number.isFinite(nis) && nis > 55) || (Number.isFinite(dil) && dil > 65)) {
    out.add("section-nutritional-fingerprint");
  }

  // 2) Metabolic environment.
  const metaBand = String(snapshot?.metabolic?.band || "").toLowerCase();
  if (metaBand === "red" || metaBand === "orange") {
    out.add("section-metabolic");
  }

  // 3) Hereditary risk (DKG) + declared genetic mutations.
  const dkgTier = String(hereditary?.risk_tier || hereditary?.tier || "").toLowerCase();
  const dkgFlag = ["moderate", "high", "very_high", "elevated"].some((t) => dkgTier.includes(t));
  const genetics = snapshot?.genomics || snapshot?.genetics || {};
  const mutations = genetics?.mutations || genetics?.variants || [];
  const hasMut = Array.isArray(mutations) && mutations.some((m) => {
    const s = typeof m === "string" ? m : (m?.gene || m?.name || "");
    return /brca1|brca2|palb2|chek2|atm|tp53|pten|cdh1/i.test(String(s));
  });
  if (dkgFlag || hasMut) {
    out.add("section-hereditary");
    if (hasMut) out.add("section-my-data");
  }

  // 4) Declared / predicted BC subtype.
  const subtype = snapshot?.subtype || snapshot?.bc_subtype;
  if (subtype && (subtype.declared || subtype.predicted || subtype.name)) {
    out.add("section-bc-subtype");
  }

  // 5) Prevention path if any pathway score is elevated.
  const pathways = snapshot?.pathway_scores || snapshot?.pathways || [];
  const highPathway = Array.isArray(pathways) && pathways.some((p) => num(p?.score) >= 0.5);
  if (highPathway) out.add("section-prevention-path");

  // 6) BI-RADS density C/D → density section.
  const birads = String(snapshot?.density?.birads || sig?.birads || "").toUpperCase();
  if (birads === "C" || birads === "D") out.add("section-density");

  return Array.from(out);
}
